import { apiTags } from 'app/store/apiTags';
import { baseQuery } from 'app/baseQuery';
import { baseApi } from 'app/baseApi';

const apiPrefix = 'activityTypePrice';

export const activityTypePriceApi = baseApi.injectEndpoints({
    reducerPath: 'activityTypePriceApi',
    tagTypes: [apiTags.ACTIVITY_TYPE_PRICE],
    baseQuery: baseQuery,
    endpoints: (builder) => ({
        getAllActivityPricing: builder.query({
            query: () => `${apiPrefix}`,
            providesTags: [apiTags.ACTIVITY_TYPE_PRICE],
        }),
        deletePrice: builder.mutation({
            query: ({ id }) => ({
                url: `${apiPrefix}`,
                method: "DELETE",
                body: { id }
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE]
        }),
        addActivityPrice: builder.mutation({
            query: (activityPrice) => ({
                url: `${apiPrefix}`,
                method: "POST",
                body: activityPrice
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE],
        }),
        updateActivityPrice: builder.mutation({
            query: (activityPrice) => ({
                url: `${apiPrefix}`,
                method: "PUT",
                body: activityPrice
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE]
        }),
        getActivityPricingByParameters: builder.query({
            query: (params) => {
                return {
                    url: `${apiPrefix}/parameters`,
                    method: 'POST',
                    body: params,
                }
            },
        }),
        getPricingManagement: builder.query({
            query: (params) => {
                return {
                    url: `${apiPrefix}/parameters`,
                    method: 'POST',
                    body: params,
                }
            },
        }),
        getPricingManagement: builder.query({
            query: (params) => {
                const queryString = Object.keys(params).map(param => {
                    if (Array.isArray(params[param])) {
                        return params[param].map(value => `${param}=${value}`).join('&');
                    } else {
                        return params[param] ? `${param}=${params[param]}` : undefined;
                    }
                }).filter(k => k).join('&');
                return `${apiPrefix}/getPricingManagement?${queryString}`
            },
        }),
        getPricingByClient: builder.query({
            query: (clientId) => `${apiPrefix}/getPricingByClient/${clientId}`,
            providesTags: [apiTags.ACTIVITY_TYPE_PRICE],
        }),
        getOtherVendorOptions: builder.query({
            query: ({ clientId, lotId, activityItemId }) => {
                const queryString = new URLSearchParams({
                    clientId,
                    lotId,
                    activityItemId,
                }).toString();
                return `${apiPrefix}/otherVendorOptions?${queryString}`;
            },
        }),
        getActivityPricingByAssetIds: builder.query({
            query: (params) => {
                return {
                    url: `${apiPrefix}/parameters/by-assets`,
                    method: 'POST',
                    body: params,
                }
            },
        }),
        getActivityPricingByLotId: builder.query({
            query: (lotId) => `${apiPrefix}/lot/${lotId}`,
            providesTags: [apiTags.ACTIVITY_TYPE_PRICE]
        }),
        getChargeForClient: builder.mutation({
            query: ({ clientId, activityType, lotId, price, inventoryUsed }) => ({
                url: `${apiPrefix}/charge`,
                method: "POST",
                body: { clientId, activityType, lotId, price, inventoryUsed },
            })
        }),
        bulkUpdatePrice: builder.mutation({
            query: (body) => ({
                url: `${apiPrefix}/bulkUpdate`,
                method: "POST",
                body
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE]
        }),
        createActivityPricingManagement: builder.mutation({
            query: (body) => ({
                url: `${apiPrefix}/createPricingManagement`,
                method: 'POST',
                body
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE, apiTags.ACTIVITY_ITEM, apiTags.MECHANICALS_CONFIG],
        }),
        updateActivityPricingManagement: builder.mutation({
            query: (body) => ({
                url: `${apiPrefix}/updatePricingManagement`,
                method: 'PUT',
                body
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE, apiTags.ACTIVITY_ITEM, apiTags.ACTIVITY_ITEM_MATERIAL, apiTags.MECHANICALS_CONFIG],
        }),
        createBundlePricingManagement: builder.mutation({
            query: (body) => ({
                url: `${apiPrefix}/createBundlePricingManagement`,
                method: 'POST',
                body
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE, apiTags.ACTIVITY_ITEM, apiTags.BUNDLE, apiTags.MECHANICALS_CONFIG, apiTags.MECHANICAL_RULE_TYPES]
        }),
        getPricingManagementByLotId: builder.query({
            query: (lotId) => `${apiPrefix}/pricingManagement/lot/${lotId}`,
            providesTags: [apiTags.ACTIVITY_TYPE_PRICE],
        }),
        getPricingByActivityType: builder.query({
            query: ({ bundleId, activityId } = {}) => {
                const queryParams = bundleId
                    ? { bundleId }
                    : activityId
                        ? { activityId }
                        : {};
                const queryString = new URLSearchParams(queryParams).toString();
                return `${apiPrefix}/getPricingByActivityType${queryString ? `?${queryString}` : ''}`
            },
            providesTags: [apiTags.ACTIVITY_TYPE_PRICE],
        }),
        getQuickLookupPricingManagement: builder.query({
            query: (params) => {
                const queryString = Object.keys(params).map(param => {
                    if (Array.isArray(params[param])) {
                        return params[param].map(value => `${param}=${value}`).join('&');
                    } else {
                        return params[param] ? `${param}=${params[param]}` : undefined;
                    }
                }).filter(k => k).join('&');
                return `${apiPrefix}/getQuickLookupPricingManagement?${queryString}`
            },
        }),
        getActivityData: builder.query({
            query: (activityItemId) => `${apiPrefix}/getDataById/${activityItemId}`,
            providesTags: [apiTags.ACTIVITY_TYPE_PRICE, apiTags.MECHANICALS_CONFIG, apiTags.ACTIVITY_ITEM, apiTags.ACTIVITY_ITEM_MATERIAL],
        }),
        getBundleData: builder.query({
            query: (bundleId) => `${apiPrefix}/getBundleDataById/${bundleId}`,
            providesTags: [apiTags.ACTIVITY_TYPE_PRICE, apiTags.MECHANICALS_CONFIG, apiTags.BUNDLE],
        }),
        updateBundlePricingManagement: builder.mutation({
            query: (body) => ({
                url: `${apiPrefix}/updateBundlePricingManagement`,
                method: 'PUT',
                body
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE, apiTags.BUNDLE, apiTags.MECHANICALS_CONFIG],
        }),
        getMaterialsPricesByActivityPriceId: builder.query({
            query: (activityPriceId) => `${apiPrefix}/materialsPrices/${activityPriceId}`,
            providesTags: [apiTags.ACTIVITY_ITEM_MATERIAL, apiTags.ACTIVITY_TYPE_PRICE, apiTags.ACTIVITY_ITEM,],
        }),
        updateActivityPricePerClientLot: builder.mutation({
            query: (body) => ({
                url: `${apiPrefix}/updatePricePerClientLot`,
                method: 'PUT',
                body
            }),
            invalidatesTags: [apiTags.ACTIVITY_TYPE_PRICE, apiTags.ACTIVITY_ITEM, apiTags.ACTIVITY_ITEM_MATERIAL, apiTags.MECHANICALS_CONFIG],
        }),
    }),
});

export const {
    useGetAllActivityPricingQuery,
    useDeletePriceMutation,
    useAddActivityPriceMutation,
    useUpdateActivityPriceMutation,
    useGetActivityPricingByParametersQuery,
    useLazyGetActivityPricingByParametersQuery,
    useGetPricingManagementQuery,
    useGetPricingByClientQuery,
    useLazyGetPricingByClientQuery, // Returns a trigger function to fetch pricing by client on demand (not automatically on mount).
    useGetOtherVendorOptionsQuery,
    useGetActivityPricingByAssetIdsQuery,
    useGetActivityPricingByLotIdQuery,
    useGetChargeForClientMutation,
    useBulkUpdatePriceMutation,
    useCreateActivityPricingManagementMutation,
    useUpdateActivityPricingManagementMutation,
    useCreateBundlePricingManagementMutation,
    useGetPricingManagementByLotIdQuery,
    useGetQuickLookupPricingManagementQuery,
    useGetPricingByActivityTypeQuery,
    useGetActivityDataQuery,
    useGetBundleDataQuery,
    useUpdateBundlePricingManagementMutation,
    useGetMaterialsPricesByActivityPriceIdQuery,
    useUpdateActivityPricePerClientLotMutation,
} = activityTypePriceApi;